import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Sparkles, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import axios from 'axios';
import ThemeToggle from '../components/ThemeToggle';

const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [formData, setFormData] = useState({});
  const [formConfig, setFormConfig] = useState([]);
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { fetchFormConfig(); }, []);

  const fetchFormConfig = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/form-config`);
      setFormConfig(response.data.fields || []);
    } catch (error) {
      console.error('獲取表單配置失敗:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        const result = await login(email, password);
        toast.success('登入成功');
        if (result?.role === 'operator') navigate('/admin/cases');
        else navigate('/create');
      } else {
        const result = await register(email, password, name);
        if (Object.keys(formData).length > 0) {
          try {
            await axios.post(
              `${process.env.REACT_APP_BACKEND_URL}/api/user/profile`,
              formData,
              { headers: { Authorization: `Bearer ${result.token}` } }
            );
          } catch (error) { console.error('保存用戶資料失敗:', error); }
        }
        if (result.requiresApproval) {
          toast.success('註冊成功！請等待管理員審批', { duration: 5000 });
          navigate('/pending-approval');
        } else {
          toast.success('註冊成功！已贈送 100 點數');
          navigate('/create');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || '操作失敗，請重試');
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    'rounded-lg h-11 bg-obsidian-800 border-obsidian-600 text-white text-sm focus:border-lime-500/60 focus:ring-0 placeholder:text-obsidian-400 transition-all';

  return (
    <div
      data-theme="dark"
      data-atelier="true"
      className="min-h-screen flex surface-obsidian overflow-hidden"
      data-testid="login-page"
    >
      {/* Floating theme toggle — top right */}
      <div className="fixed top-4 right-4 z-50">
        <ThemeToggle />
      </div>

      {/* Top neon strip */}
      <div className="fixed top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-lime-500 to-transparent z-50" />

      {/* Left branded panel — desktop only */}
      <aside className="hidden lg:flex w-[46%] relative overflow-hidden border-r border-obsidian-600">
        {/* Grid */}
        <div
          className="absolute inset-0 opacity-[0.1] pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
            maskImage: 'radial-gradient(ellipse 70% 60% at 40% 50%, black 0%, transparent 75%)',
            WebkitMaskImage: 'radial-gradient(ellipse 70% 60% at 40% 50%, black 0%, transparent 75%)',
          }}
        />
        {/* Halo */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse 50% 45% at 30% 45%, rgba(255,84,54,0.16) 0%, transparent 70%)',
          }}
        />

        <div className="relative w-full flex flex-col justify-between p-12 xl:p-16 z-10">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 bg-lime-500 rounded-md flex items-center justify-center"
              style={{ boxShadow: '0 0 18px rgba(255,84,54,0.45)' }}
            >
              <Sparkles className="w-4 h-4 text-obsidian-900" strokeWidth={2.5} />
            </div>
            <div className="flex items-baseline gap-2">
              <span className="font-display font-semibold text-base text-white tracking-tight">Reelscript</span>
              <span className="label-micro text-lime-500">AI</span>
            </div>
          </div>

          <div>
            <div className="label-micro text-lime-500 mb-5 flex items-center gap-3">
              <span className="h-px w-8 bg-lime-500/60" />
              WELCOME · BACK
              <span className="w-1.5 h-1.5 rounded-full bg-lime-500 animate-pulse" />
            </div>
            <h1
              className="font-display font-semibold text-white tracking-tighter leading-[1.02] mb-6"
              style={{ fontSize: 'clamp(40px, 5vw, 72px)' }}
            >
              讓 AI 幫您打造
              <br />
              <span className="text-lime-500">爆款</span>內容
            </h1>
            <p className="text-obsidian-200 text-base leading-relaxed max-w-md">
              專為華語創作者設計的 AI 助理 — 節省 90% 創作時間，專注於你的個人品牌。
            </p>
          </div>

          <div className="flex items-center gap-6 label-micro text-obsidian-400">
            <span>10M+ 累計流量</span>
            <span>·</span>
            <span>90% 時間節省</span>
            <span>·</span>
            <span>24/7 待命</span>
          </div>
        </div>
      </aside>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-10 relative">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.25, 1, 0.5, 1] }}
          className="w-full max-w-md"
        >
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-10">
            <div
              className="inline-flex items-center justify-center w-12 h-12 bg-lime-500 rounded-lg mb-4"
              style={{ boxShadow: '0 0 20px rgba(255,84,54,0.45)' }}
            >
              <Sparkles className="w-5 h-5 text-obsidian-900" strokeWidth={2.5} />
            </div>
            <div className="flex items-baseline gap-2 justify-center">
              <span className="font-display font-semibold text-lg text-white tracking-tight">Reelscript</span>
              <span className="label-micro text-lime-500">AI</span>
            </div>
          </div>

          {/* Heading */}
          <div className="mb-8">
            <div className="label-micro text-obsidian-400 mb-3 flex items-center gap-3">
              <span className="h-px w-6 bg-obsidian-600" />
              {isLogin ? 'SIGN · IN' : 'CREATE · ACCOUNT'}
            </div>
            <h2 className="font-display font-semibold text-white text-3xl md:text-4xl tracking-tighter leading-tight">
              {isLogin ? '歡迎回來' : '建立帳號'}
            </h2>
            <p className="text-obsidian-200 text-sm mt-2">
              {isLogin ? '登入以繼續使用 Reelscript AI' : '免費註冊，立即贈送 100 點數'}
            </p>
          </div>

          {/* Tabs */}
          <div className="flex gap-0 mb-8 border-b border-obsidian-600">
            <button
              type="button"
              onClick={() => setIsLogin(true)}
              className={`relative pb-3 px-1 mr-6 text-sm font-medium transition-colors ${
                isLogin ? 'text-white' : 'text-obsidian-400 hover:text-obsidian-100'
              }`}
              data-testid="login-tab-btn"
            >
              登入
              {isLogin && (
                <motion.span
                  layoutId="auth-tab-underline"
                  className="absolute -bottom-px left-0 right-0 h-0.5 bg-lime-500"
                  style={{ boxShadow: '0 0 8px rgba(255,84,54,0.6)' }}
                />
              )}
            </button>
            <button
              type="button"
              onClick={() => setIsLogin(false)}
              className={`relative pb-3 px-1 text-sm font-medium transition-colors ${
                !isLogin ? 'text-white' : 'text-obsidian-400 hover:text-obsidian-100'
              }`}
              data-testid="register-tab-btn"
            >
              註冊
              {!isLogin && (
                <motion.span
                  layoutId="auth-tab-underline"
                  className="absolute -bottom-px left-0 right-0 h-0.5 bg-lime-500"
                  style={{ boxShadow: '0 0 8px rgba(255,84,54,0.6)' }}
                />
              )}
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <>
                <div>
                  <Label htmlFor="name" className="label-micro text-obsidian-300 mb-2 block">NAME</Label>
                  <Input
                    id="name" type="text" value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className={inputClass}
                    placeholder="請輸入姓名"
                    data-testid="name-input"
                  />
                </div>
                {formConfig.map((field) => (
                  <div key={field.id}>
                    <Label htmlFor={field.id} className="label-micro text-obsidian-300 mb-2 block">
                      {field.label}
                      {field.required && <span className="text-red-400 ml-1">*</span>}
                    </Label>
                    {field.field_type === 'textarea' ? (
                      <Textarea
                        id={field.id}
                        value={formData[field.id] || ''}
                        onChange={(e) => setFormData({ ...formData, [field.id]: e.target.value })}
                        required={field.required}
                        className={`${inputClass} min-h-[80px] h-auto`}
                        placeholder={`請輸入${field.label}`}
                      />
                    ) : field.field_type === 'select' ? (
                      <select
                        id={field.id}
                        value={formData[field.id] || ''}
                        onChange={(e) => setFormData({ ...formData, [field.id]: e.target.value })}
                        required={field.required}
                        className="w-full rounded-lg h-11 bg-obsidian-800 border border-obsidian-600 text-white text-sm focus:border-lime-500/60 focus:outline-none px-3 transition-all"
                      >
                        <option value="">請選擇</option>
                        {field.options?.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <Input
                        id={field.id} type="text"
                        value={formData[field.id] || ''}
                        onChange={(e) => setFormData({ ...formData, [field.id]: e.target.value })}
                        required={field.required}
                        className={inputClass}
                        placeholder={`請輸入${field.label}`}
                      />
                    )}
                  </div>
                ))}
              </>
            )}

            <div>
              <Label htmlFor="email" className="label-micro text-obsidian-300 mb-2 block">EMAIL</Label>
              <Input
                id="email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                required className={inputClass}
                placeholder="email@example.com"
                data-testid="email-input"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="password" className="label-micro text-obsidian-300 block">PASSWORD</Label>
                {isLogin && (
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="label-micro text-lime-500 hover:text-lime-400 transition-colors"
                    data-testid="forgot-password-link"
                  >
                    忘記密碼？
                  </button>
                )}
              </div>
              <Input
                id="password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)}
                required className={inputClass}
                placeholder="請輸入密碼"
                data-testid="password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full rounded-full h-12 btn-neon text-sm font-semibold mt-3"
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? '處理中…' : (
                <span className="flex items-center justify-center gap-2">
                  {isLogin ? '登入' : '註冊並贈送 100 點數'}
                  <ArrowRight className="w-3.5 h-3.5" />
                </span>
              )}
            </Button>
          </form>

          <div className="mt-7 text-center label-micro text-obsidian-400">
            登入即表示您同意我們的服務條款
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;
